<template>
  <Breadcrumb></Breadcrumb>
  <div v-if="saleListingAgreement">
    <div class="grid grid-cols-3 items-start">
      <div class="col-span-2">
        <!-- Title block -->
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow">
          <div class="px-5 py-5 sm:py-6">
            <div class="flex gap-2 items-center">
              <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <BriefcaseIcon class="h-8 w-8 text-gray-700"></BriefcaseIcon>
              </div>
              <SaleListingAgreementId :saleListingAgreement="saleListingAgreement" />
              <div>
                <RiskScoringLabel :level="titleRiskLevel" />
              </div>
              <router-link class="text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'edit-sale-listing-agreement', params: { id: saleListingAgreement.id } }">
                <PencilSquareIcon class="size-5" aria-hidden="true" />
              </router-link>
            </div>
            <div v-if="pendingApprovals.length" class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <ExclamationTriangleIcon class="h-5 w-5 text-yellow-600 mt-0.5" />
              <p class="text-sm text-yellow-800">
                <span v-for="(party, index) in pendingApprovals" :key="party.id">
                  <strong>{{ party.name }}</strong> ({{ party.role }})<span v-if="index < pendingApprovals.length - 1">, </span>
                </span>
                {{ $ngettext("n'a pas encore de profil de risque approuvé", "n'ont pas encore de profil de risque approuvé", pendingApprovals.length) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Properties block -->
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow mt-4">
          <div class="px-5 py-5 sm:py-6">
            <span class="text-m font-semibold text-gray-900">{{ $gettext("Propriété(s)") }}</span>
            <ConfirmationDialog
              v-model="purchaseAgreementDialogOpen"
              :title="$gettext('Supprimer le compromis')"
              :message="$gettext('Êtes-vous sûr de vouloir supprimer ce compromis ? Cette action est irréversible.')"
              :confirm-text="$gettext('Supprimer')"
              :cancel-text="$gettext('Annuler')"
              @confirm="deletePurchaseAgreement(currentId, currentSaleListingAgreementId, currentPurchaseOfferId)"
            />
            <ConfirmationDialog
              v-model="purchaseOfferDialogOpen"
              :title="$gettext('Supprimer cette offre')"
              :message="$gettext('Êtes-vous sûr de vouloir supprimer cette offre ? Cette action est irréversible.')"
              :confirm-text="$gettext('Supprimer')"
              :cancel-text="$gettext('Annuler')"
              @confirm="deletePurchaseOffer(currentId, currentSaleListingAgreementId, currentPropertyId)"
            />
            <div v-for="saleListingAgreementProperty in saleListingAgreement?.relationships?.saleListingAgreementProperties?.data" :key="saleListingAgreementProperty.id" class="divide-y divide-gray-200 overflow-hidden border border-gray-100 rounded-lg bg-white shadow p-4 mb-2 mt-2">
              <h3 class="text-md font-semibold text-gray-900 flex items-center justify-between w-full">
                <div class="flex items-center">
                  <HomeIcon class="h-6 w-6 text-gray-700 flex-shrink-0 mr-2" />
                  <span class="text-gray-900">
                    {{ saleListingAgreementProperty.relationships.property.data.attributes.addressStreet }},
                    {{ saleListingAgreementProperty.relationships.property.data.attributes.addressZipcode }}
                    {{ saleListingAgreementProperty.relationships.property.data.attributes.addressCity }}
                  </span>
                </div>

                <span class="text-sm text-gray-600 ml-4">
                  {{ saleListingAgreementProperty.relationships.property.data.relationships.propertyType.data.attributes.name }} ·
                  {{ saleListingAgreementProperty.attributes.listingPrice }} €
                </span>
              </h3>

              <div class="mt-2">
                <iframe
                  class="w-full h-48 rounded-md border"
                  loading="lazy"
                  allowfullscreen
                  referrerpolicy="no-referrer-when-downgrade"
                  :src="`https://www.google.com/maps?q=${encodeURIComponent(
                    saleListingAgreementProperty.relationships.property.data.attributes.addressStreet +
                    ' ' +
                    saleListingAgreementProperty.relationships.property.data.attributes.addressZipcode +
                    ' ' +
                    saleListingAgreementProperty.relationships.property.data.attributes.addressCity
                  )}&output=embed`">
               </iframe>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-lg bg-white shadow mt-4">
                  <div v-if="purchaseOffer(saleListingAgreementProperty)?.id">
                    <strong>{{ $gettext("Offre d'achat") }}</strong>
                    <DescriptionList class="mt-4">
                      <DescriptionTerm>{{ $gettext("Date") }}</DescriptionTerm>
                      <DescriptionDetails>
                        {{ parseDate(purchaseOffer(saleListingAgreementProperty).attributes.signatureDate) }}
                      </DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Prix") }}</DescriptionTerm>
                      <DescriptionDetails>
                        <MoneySpan :value="purchaseOffer(saleListingAgreementProperty).attributes.price" />
                      </DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Notaire") }}</DescriptionTerm>
                      <DescriptionDetails>{{ purchaseOffer(saleListingAgreementProperty).relationships.notary.data.attributes?.name }}</DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Acheteur(s)") }}</DescriptionTerm>
                      <DescriptionDetails>
                        <ul class="mb-1 space-y-1">
                          <li
                            v-for="buyer in purchaseOffer(saleListingAgreementProperty).relationships.buyers.data"
                            :key="buyer.id"
                          >
                            <CustomerLink :customer="buyer" :showRiskLabel="true"></CustomerLink>
                          </li>
                        </ul>
                      </DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Actions") }}</DescriptionTerm>
                      <DescriptionDetails class="flex">
                        <router-link
                          :to="{ name: 'edit-purchase-offer', params: { id: purchaseOffer(saleListingAgreementProperty)?.id }, query: { saleListingAgreementId: saleListingAgreement.id, propertyId: saleListingAgreementProperty.relationships.property.data.id }}"
                          class="mr-2 inline-flex items-center rounded-md border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                        >
                          <PencilIcon class="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
                          {{ $gettext("Modifier") }}
                        </router-link>
                        <button
                          @click="askDeletePurchaseOffer(purchaseOffer(saleListingAgreementProperty)?.id, saleListingAgreement.id, saleListingAgreementProperty.relationships.property.data.id)"
                          type="button"
                          class="inline-flex items-center rounded-md border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                        >
                          <TrashIcon class="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
                          {{ $gettext("Supprimer") }}
                        </button>
                      </DescriptionDetails>
                    </DescriptionList>
                  </div>
                  <div v-else>
                    <router-link :to="{ name: 'new-purchase-offer', query: {saleListingAgreementId: saleListingAgreement.id, propertyId: saleListingAgreementProperty.relationships.property.data.id}}"
                        class="inline-flex items-center rounded-md bg-indigo-600 mt-4 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                        <PlusIcon class="-ml-0.5 mr-1.5 size-5 rounded-md border border-white-900" aria-hidden="true" />
                        {{ $gettext("Nouvelle offre d'achat") }}
                      </router-link>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-lg bg-white shadow mt-4">
                  <div v-if="purchaseOffer(saleListingAgreementProperty)?.id">
                    <div v-if="purchaseAgreement(saleListingAgreementProperty)?.id">
                      <strong>{{ $gettext("Compromis de vente") }}</strong>
                      <DescriptionList class="mt-4">
                        <DescriptionTerm>{{ $gettext("Date") }}</DescriptionTerm>
                        <DescriptionDetails> {{ parseDate(purchaseAgreement(saleListingAgreementProperty).attributes.signatureDate) }} </DescriptionDetails>

                        <DescriptionTerm>{{ $gettext("Prix") }}</DescriptionTerm>
                        <DescriptionDetails>
                          <MoneySpan :value="purchaseAgreement(saleListingAgreementProperty).attributes.finalPrice" />
                        </DescriptionDetails>

                        <DescriptionTerm>{{ $gettext("Accompte") }}</DescriptionTerm>
                        <DescriptionDetails>
                          <MoneySpan :value="purchaseAgreement(saleListingAgreementProperty).attributes.deposit" />
                        </DescriptionDetails>

                        <DescriptionTerm>{{ $gettext("Solde") }}</DescriptionTerm>
                        <DescriptionDetails>
                          <MoneySpan :value="purchaseAgreement(saleListingAgreementProperty).attributes.balance" />
                        </DescriptionDetails>

                        <DescriptionTerm>{{ $gettext("Compte de l\'acheteur") }}</DescriptionTerm>
                        <DescriptionDetails>{{ purchaseAgreement(saleListingAgreementProperty).attributes.buyerAccountNumber }} ({{ purchaseAgreement(saleListingAgreementProperty).relationships.buyerAccountCountry.data.attributes.name }})</DescriptionDetails>

                        <DescriptionTerm>{{ $gettext("Actions") }}</DescriptionTerm>
                        <DescriptionDetails class="flex">
                          <router-link
                          :to="{ name: 'edit-purchase-agreement', params: { id: purchaseAgreement(saleListingAgreementProperty)?.id }, query: { saleListingAgreementId: saleListingAgreement.id, purchaseOfferId: purchaseOffer(saleListingAgreementProperty)?.id }}"
                          class="mr-2 inline-flex items-center rounded-md border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                        >
                          <PencilIcon class="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
                          {{ $gettext("Modifier") }}
                        </router-link>
                        <button
                          @click="askDeletePurchaseAgreement(purchaseAgreement(saleListingAgreementProperty)?.id, saleListingAgreement.id, purchaseOffer(saleListingAgreementProperty)?.id)"
                          type="button"
                          class="inline-flex items-center rounded-md border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                        >
                          <TrashIcon class="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
                          {{ $gettext("Supprimer") }}
                        </button>
                        </DescriptionDetails>
                      </DescriptionList>
                    </div>
                    <div v-else>
                      <router-link :to="{ name: 'new-purchase-agreement', query: {saleListingAgreementId: saleListingAgreement.id, purchaseOfferId: purchaseOffer(saleListingAgreementProperty)?.id}}"
                        class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                        <PlusIcon class="-ml-0.5 mr-1.5 size-5 rounded-md border border-white-900" aria-hidden="true" />
                        {{ $gettext("Nouveau compromis de vente") }}
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-span-1">
      <!-- Summary block -->
      <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 ml-4">
        <div class="px-5 py-5 sm:py-6">
          <span class="text-m font-semibold text-gray-900">{{ $gettext("Aperçu") }}</span>
        </div>
        <div class="px-5 py-4 sm:py-6" >
          <DescriptionList>
            <DescriptionTerm>{{ $gettext("Commission") }}</DescriptionTerm>
            <DescriptionDetails>{{ saleListingAgreement.attributes.commission }} {{ saleListingAgreement.relationships.commissionType.data?.attributes?.name }}</DescriptionDetails>

            <DescriptionTerm>{{ $gettext("Date de fin") }}</DescriptionTerm>
            <DescriptionDetails>{{ parseDate(saleListingAgreement.attributes.endAt) }}</DescriptionDetails>

            <DescriptionTerm>{{ $gettext("Notaire") }}</DescriptionTerm>
            <DescriptionDetails>{{ saleListingAgreement.relationships.notary.data.attributes?.name }}</DescriptionDetails>

            <DescriptionTerm>{{ $gettext("Vendeur(s)") }}</DescriptionTerm>
            <DescriptionDetails>
              <ul>
                <li
                  v-for="saleListingAgreementSeller in saleListingAgreement.relationships.saleListingAgreementSellers.data"
                  :key="saleListingAgreementSeller.id"
                >
                  <CustomerLink :customer="saleListingAgreementSeller" :showRiskLabel="true" :truncate="true"></CustomerLink>
                </li>
              </ul>
            </DescriptionDetails>
          </DescriptionList>
        </div>
      </div>
        <!-- Risk block -->
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-5 py-5 sm:py-6 flex items-center justify-between">

            <span class="text-m font-semibold text-gray-900">{{ $gettext("Profil de risque") }}</span>
            <router-link class="p-2 text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'sale-listing-agreement-risk-profile', params: { id: saleListingAgreementId } }">
              <PencilSquareIcon class="size-5" aria-hidden="true" />
            </router-link>
          </div>
          <div class="px-5 py-5 sm:py-6">
            <div v-if="riskScoring !== null">
              <RiskScoringDescription :riskScoring="riskScoring"></RiskScoringDescription>
            </div>
            <div class="h-16 w-full flex items-center justify-center" v-else>
              <RiskScoringLabel cssClasses="h-full w-full flex items-center justify-center font-bold" :level="saleListingAgreement.relationships?.lastRiskScoring?.data?.attributes?.scoreLevel ?? undefined" />
            </div>
          </div>
          <RiskScoringChart v-if="riskScoring" :riskScoring="riskScoring" />
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6" v-if="documents">
            <DocumentList v-if="documents" :documents="documents" />
          </div>
        </div>
        <div class="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow col-span-1 m-4">
          <div class="px-4 py-5 sm:px-6">
            <ReportList :reports="saleListingAgreementReportStore.all" report-type="saleListingAgreement" @build="onBuildReport" @delete="onDeleteReport"></ReportList>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Breadcrumb from "@/components/layout/BreadcrumbNavigation.vue";

import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import RiskScoringDescription from "@/components/risk-scorings/RiskScoringDescription.vue";
import RiskScoringChart from "@/components/risk-scorings/RiskScoringChart.vue";
import { PlusIcon, HomeIcon, TrashIcon, PencilIcon, BriefcaseIcon, PencilSquareIcon, ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { useRiskScoring } from "@/stores/riskScoring";
import { useSaleListingAgreementStore } from "@/stores/saleListingAgreement";
import { usePurchaseAgreementStore } from "@/stores/purchaseAgreement";
import { usePurchaseOfferStore } from "@/stores/purchaseOffer";
import CustomerLink from "@/components/customers/CustomerLink.vue";
import { DescriptionList, DescriptionTerm, DescriptionDetails, ConfirmationDialog, MoneySpan } from "@/components/ui";
import { useDate } from "@/utils/stores/date";
const { parseDate } = useDate();
import ReportList from "@/components/reports/ReportList.vue";
import DocumentList from "@/components/documents/DocumentList.vue";
import { useSaleListingAgreementReportStore } from "@/stores/saleListingAgreementReport";
import { useDocumentUtils } from "@/utils/documents";
import { useSaleListingAgreementPartiesUtils } from "@/utils/sale_listing_agreement_parties";
import SaleListingAgreementId from "@/components/sale-listing-agreements/SaleListingAgreementId.vue"

const route = useRoute();
const saleListingAgreementId = route.params.id;
const saleListingAgreement = ref(null);
const saleListingAgreementStore = useSaleListingAgreementStore();
const purchaseAgreementStore = usePurchaseAgreementStore();
const purchaseOfferStore = usePurchaseOfferStore();
const saleListingAgreementReportStore = useSaleListingAgreementReportStore(saleListingAgreementId);

const purchaseAgreementDialogOpen = ref(false);
const purchaseOfferDialogOpen = ref(false);
const currentId = ref(null);
const currentSaleListingAgreementId = ref(null);
const currentPurchaseOfferId = ref(null);
const currentPropertyId = ref(null);
const documents = ref(null)

const pendingApprovals = ref([])

const titleRiskLevel = computed(() => {
  const lastRiskScoring = saleListingAgreement.value?.relationships?.lastRiskScoring?.data;
  const riskScoringConfirmed = lastRiskScoring?.attributes?.reviewApproval === true;
  const hasPendingPartyApprovals = (pendingApprovals.value?.length ?? 0) > 0;

  if (!riskScoringConfirmed || hasPendingPartyApprovals) {
    return undefined;
  }

  return lastRiskScoring?.attributes?.scoreLevel;
});

const riskScoringStore = useRiskScoring("sale-listing-agreements", saleListingAgreementId);
const riskScoring = computed(() => {
  return riskScoringStore.latest;
});

const reportIncludes = [
  "saleListingAgreementSellers",
  "saleListingAgreementProperties",
  "saleListingAgreementReports",
  "notary",
  "commissionType",
  "saleListingAgreementDocuments",
  "lastRiskScoring"
];

const askDeletePurchaseOffer = (id, saleListingAgreementId, propertyId) => {
  currentId.value = id;
  currentSaleListingAgreementId.value = saleListingAgreementId;
  currentPropertyId.value = propertyId;
  purchaseOfferDialogOpen.value = true;
};

const purchaseOffer = (saleListingAgreementProperty) => {
  return saleListingAgreementProperty?.relationships?.property?.data?.relationships?.purchaseOffers?.data?.[0] ?? null;
};

const purchaseAgreement = (saleListingAgreementProperty) => {
  return saleListingAgreementProperty?.relationships?.property?.data?.relationships?.purchaseOffers?.data?.[0]?.relationships?.purchaseAgreements?.data?.[0] ?? null;
};

const askDeletePurchaseAgreement = (id, saleListingAgreementId, purchaseOfferId) => {
  currentId.value = id;
  currentSaleListingAgreementId.value = saleListingAgreementId;
  currentPurchaseOfferId.value = purchaseOfferId;
  purchaseAgreementDialogOpen.value = true;
};

const deletePurchaseAgreement = async (id, saleListingAgreementId, purchaseOfferId) => {
  await purchaseAgreementStore.destroy(id, saleListingAgreementId, purchaseOfferId);
  loadSaleListingAgreement();
};

const deletePurchaseOffer = async (id, saleListingAgreementId, propertyId) => {
  await purchaseOfferStore.destroy(id, saleListingAgreementId, propertyId);
  loadSaleListingAgreement();
};

const loadSaleListingAgreement = async () => {
  saleListingAgreement.value = await saleListingAgreementStore.fetchOne(saleListingAgreementId);
  documents.value = useDocumentUtils().extractDocuments(saleListingAgreement.value);
  pendingApprovals.value = useSaleListingAgreementPartiesUtils().extractPendingReviewApprovals(saleListingAgreement.value);
};

const onBuildReport = async () => {
  await saleListingAgreementStore.buildReport(saleListingAgreementId, () => {
    saleListingAgreementReportStore.fetchAll();
  }, null, reportIncludes);
};

const onDeleteReport = async (report) => {
  await saleListingAgreementStore.deleteReport(report.id, saleListingAgreementId, () => {
    saleListingAgreementReportStore.fetchAll();
  });
};

onMounted(async () => {
  await loadSaleListingAgreement();
  await saleListingAgreementReportStore.fetchAll();
  await saleListingAgreementReportStore.watchResourceChanges(() => {
    saleListingAgreementReportStore.fetchAll();
  });
});

</script>
